import { Fragment, type JSX, useEffect, useState } from 'react';

import {
  ausNeuemAuftrag,
  kontaktauswahlZumKunden,
  offenePosten,
  type Direktrabatt,
  type Direktzeile,
  type Rechnungskontakt,
} from '../api/rechnungen';
import { zeile as kundenzeile, type Kundenzeile } from '../api/kunden';
import {
  preisauskunft,
  steuerschluessel as steuerschluesselListe,
  type Artikelzeile,
  type Steuerschluessel,
} from '../api/produkte';
import { ApiFehler } from '../api/fehler';
import { alsEuro, alsProzent } from '../betrag';
import { Abschnitt, Feldraster } from '../komponenten/Akte';
import { Erfassung } from '../komponenten/Erfassung';
import { Meldung } from '../komponenten/Meldung';
import { Artikelwahl } from '../produkt/Artikelwahl';
import { Kundenfeld, Kundensuchfenster } from '../suche/Kundenwahl';
import { inPreisfuehrung, restbetrag, summeDerBetraege, vorschausummen, zeilenbetrag } from './direktsummen';
import { GutscheinAnrechnen, type BgAnrechnung, type WertAnrechnung } from './GutscheinAnrechnen';

/** Der heutige Tag als ISO-Datum, ohne Zeitzonensprung. */
function heute(): string {
  const j = new Date();
  return `${j.getFullYear()}-${String(j.getMonth() + 1).padStart(2, '0')}-${String(j.getDate()).padStart(2, '0')}`;
}

const PREIS = /^\d{1,10}(\.\d{1,4})?$/;
const PROZENT = /^\d{1,3}(\.\d{1,2})?$/;
const BETRAG = /^\d{1,10}(\.\d{1,2})?$/;

/** CRM-174: ein Nachlass, wie er in der Maske steht — Art und Wert als Text. */
type Nachlass = { readonly art: 'kein' | 'prozent' | 'betrag'; readonly wert: string };

const KEIN_NACHLASS: Nachlass = { art: 'kein', wert: '' };

type Zeile = {
  readonly nr: number;
  readonly art: 'artikel' | 'freitext';
  readonly artikel: Artikelzeile | undefined;
  readonly bezeichnung: string;
  readonly menge: string;
  /** In der Preisführung des Belegs, wie die Maske ihn zeigt. */
  readonly einzelpreis: string;
  /** Bei Artikeln: ob der Preis von Hand steht. Sonst gilt der Katalog (PRF-16). */
  readonly vonHand: boolean;
  readonly steuerschluessel: string;
  readonly steuerProzent: string;
  readonly hinweis: string | null;
  /** CRM-174: Nachlass der Zeile und eigenes Leistungsdatum (leer: Zeitraum der Rechnung). */
  readonly nachlass: Nachlass;
  readonly leistungVon: string;
  readonly leistungBis: string;
};

/** ANG-02, AUF-08: ein leerer Nachlass fehlt im Aufruf; ein unlesbarer sperrt das Anlegen. */
function alsRabatt(n: Nachlass): Direktrabatt | undefined | null {
  if (n.art === 'kein' || n.wert.trim() === '') return undefined;
  const wert = preisLesbar(n.wert);
  if (n.art === 'prozent') return PROZENT.test(wert) && Number(wert) <= 100 ? { art: 'prozent', prozent: wert } : null;
  return BETRAG.test(wert) ? { art: 'betrag', betrag: wert } : null;
}

function nachlassLesbar(n: Nachlass): string {
  return n.art === 'prozent' ? alsProzent(preisLesbar(n.wert)) : alsEuro(preisLesbar(n.wert));
}

/** RND-08: Privatkundenbelege führen den Bruttobetrag (AUF-01 übernimmt das). */
function preisfuehrungFuer(kunde: Kundenzeile | undefined): 'netto' | 'brutto' {
  return kunde?.kundenart === 'privat' ? 'brutto' : 'netto';
}

function preisLesbar(wert: string): string {
  return wert.trim().replace(',', '.');
}

function zeileVollstaendig(z: Zeile): boolean {
  const menge = Number(z.menge);
  if (!Number.isInteger(menge) || menge < 1) return false;
  if (alsRabatt(z.nachlass) === null) return false;
  if (z.leistungBis !== '' && (z.leistungVon === '' || z.leistungBis < z.leistungVon)) return false;
  if (z.art === 'artikel') return z.artikel !== undefined && PREIS.test(preisLesbar(z.einzelpreis));
  return z.bezeichnung.trim() !== '' && PREIS.test(preisLesbar(z.einzelpreis)) && z.steuerschluessel !== '';
}

function alsDirektzeile(z: Zeile): Direktzeile {
  const rabatt = alsRabatt(z.nachlass);
  // BP-33, CRM-174: ohne eigenes Datum gilt der Zeitraum der Rechnung.
  const zusatz = {
    ...(rabatt == null ? {} : { rabatt }),
    ...(z.leistungVon === '' ? {} : { leistungVon: z.leistungVon }),
    ...(z.leistungVon === '' || z.leistungBis === '' ? {} : { leistungBis: z.leistungBis }),
  };
  if (z.art === 'artikel') {
    const form = z.artikel?.vertriebsformen[0];
    return {
      art: 'artikel',
      artikel: z.artikel?.id ?? null,
      ...(form === undefined ? {} : { vertriebsform: form }),
      bezeichnung: null,
      menge: Number(z.menge),
      // PRF-16: unverändert schickt die Maske keinen Preis, der Server sucht ihn selbst.
      einzelpreis: z.vonHand ? preisLesbar(z.einzelpreis) : null,
      steuerschluessel: null,
      ...zusatz,
    };
  }
  return {
    art: 'freitext',
    artikel: null,
    bezeichnung: z.bezeichnung.trim(),
    menge: Number(z.menge),
    einzelpreis: preisLesbar(z.einzelpreis),
    steuerschluessel: z.steuerschluessel,
    ...zusatz,
  };
}

/**
 * Die Erfassung „Neue Rechnung" (BUC-75, M-61; Auftrag CRM-152).
 *
 * ZUERST DER AUFTRAG, DANN DIE RECHNUNG. Eine freie Rechnung am Auftrag vorbei
 * gibt es nicht: „Anlegen und abrechnen" legt einen Auftrag an (Herkunft
 * Innendienst, Zahlungsbedingung aus der Kundenkondition nach AUF-02 und
 * AUF-21), trägt die Positionen ein, bestätigt ihn und erzeugt den
 * Rechnungsentwurf — in einer Transaktion auf dem Server. Danach öffnet sich
 * der Entwurf; Festschreiben bleibt ein eigener Schritt mit Rückfrage
 * (RNG-03, RNG-04).
 *
 * Die Positionen kennen, was POST /auftraege/:id/positionen kennt: Artikel
 * oder Freitext, Menge, Preis, Steuer. Termine und Abschnitte gehören in die
 * Auftragsakte.
 *
 * DAZU, SEIT CRM-174, alles im selben Aufruf und damit in derselben
 * Transaktion: Nachlass je Zeile und auf die ganze Rechnung (über die
 * Rabattlogik des Auftrags; die Kundenkondition gilt vorab), Leistungsdatum je
 * Zeile, Kopf- und Schlusstext, ein Ansprechpartner als Empfänger (KUN-08,
 * BUC-103) und Gutscheine beider Arten. Die Maske rechnet davon nichts: Sie
 * zeigt, was erfasst ist, und der Entwurf rechnet verbindlich — es gibt
 * keinen zweiten Rechenweg.
 */
export function NeueRechnung({
  anfangsKunde,
  beiAngelegt,
  beiAbbruch,
}: {
  readonly anfangsKunde?: string;
  readonly beiAngelegt: (rechnung: string) => void;
  readonly beiAbbruch: () => void;
}): JSX.Element {
  // M-07: der Kunde über das Suchfenster, wie in den anderen Anlegen-Masken (Auftrag CRM-172).
  const [waehlt, setzeWaehlt] = useState(false);
  const [kunde, setzeKunde] = useState<Kundenzeile | undefined>(undefined);
  const [betreff, setzeBetreff] = useState('');
  const [referenz, setzeReferenz] = useState('');
  const [leistungVon, setzeLeistungVon] = useState(heute());
  const [leistungBis, setzeLeistungBis] = useState('');
  const [zeilen, setzeZeilen] = useState<readonly Zeile[]>([]);
  const [naechste, setzeNaechste] = useState(1);
  const [wahlFuer, setzeWahlFuer] = useState<number | undefined>(undefined);
  const [steuern, setzeSteuern] = useState<readonly Steuerschluessel[]>([]);
  const [gesamtNachlass, setzeGesamtNachlass] = useState<Nachlass>(KEIN_NACHLASS);
  const [kopftext, setzeKopftext] = useState('');
  const [fusstext, setzeFusstext] = useState('');
  const [kontakte, setzeKontakte] = useState<readonly Rechnungskontakt[]>([]);
  const [empfaenger, setzeEmpfaenger] = useState('');
  const [guthaben, setzeGuthaben] = useState<string | undefined>(undefined);
  const [bgGutscheine, setzeBgGutscheine] = useState<readonly BgAnrechnung[]>([]);
  const [wertgutscheine, setzeWertgutscheine] = useState<readonly WertAnrechnung[]>([]);
  const [gutscheinOffen, setzeGutscheinOffen] = useState(false);
  const [laeuft, setzeLaeuft] = useState(false);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);

  const fuehrung = preisfuehrungFuer(kunde);

  useEffect(() => {
    if (anfangsKunde === undefined || anfangsKunde === '') return;
    void kundenzeile(anfangsKunde)
      .then(setzeKunde)
      .catch(() => undefined);
  }, [anfangsKunde]);

  /*
   * KUN-08: die Ansprechpartner des Kunden als Empfänger zur Wahl. DAT-12: ein
   * Guthaben des Kunden wird nur genannt — verrechnet wird es als eigener
   * Vorgang an den offenen Posten, nicht beim Anlegen (Auftrag CRM-174).
   */
  useEffect(() => {
    setzeKontakte([]);
    setzeEmpfaenger('');
    setzeGuthaben(undefined);
    if (kunde === undefined) return;
    let gilt = true;
    void kontaktauswahlZumKunden(kunde.id)
      .then((k) => {
        if (!gilt) return;
        setzeKontakte(k);
        setzeEmpfaenger(k.find((x) => x.rechnungsempfaenger)?.kontaktrolle ?? '');
      })
      .catch(() => undefined);
    void offenePosten({ kunde: kunde.id })
      .then((posten) => {
        // Ein Guthaben steht als negativer Saldo in den offenen Posten.
        const guthabenposten = posten.filter((p) => p.art === 'guthaben').map((p) => p.offenerBetrag.replace('-', ''));
        if (gilt && guthabenposten.length > 0) setzeGuthaben(summeDerBetraege(guthabenposten));
      })
      .catch(() => undefined);
    return () => {
      gilt = false;
    };
  }, [kunde]);

  useEffect(() => {
    void steuerschluesselListe()
      .then(setzeSteuern)
      .catch(() => setzeSteuern([]));
  }, []);

  function aendere(nr: number, teil: Partial<Zeile>): void {
    setzeZeilen((alt) => alt.map((z) => (z.nr === nr ? { ...z, ...teil } : z)));
  }

  /** PRF-16: der Katalogpreis für Kunde, Menge und Leistungstag — nur zur Anzeige. */
  async function katalogpreis(z: Zeile, artikel: Artikelzeile, k: Kundenzeile | undefined): Promise<void> {
    const form = artikel.vertriebsformen[0];
    try {
      const auskunft = await preisauskunft(artikel.id, {
        kunde: k?.id ?? null,
        ...(form === undefined ? {} : { vertriebsform: form }),
        menge: Math.max(1, Number(z.menge) || 1),
        stichtag: leistungVon,
      });
      const p = auskunft.preis;
      if (p === null) {
        aendere(z.nr, { einzelpreis: '', vonHand: true, steuerschluessel: '', steuerProzent: '', hinweis: 'Kein Katalogpreis — bitte eintragen.' });
        return;
      }
      aendere(z.nr, {
        einzelpreis: inPreisfuehrung(p.staffelpreis ?? p.einzelpreis, p.istBrutto, p.steuerProzent, preisfuehrungFuer(k)),
        vonHand: false,
        steuerschluessel: p.steuerschluessel,
        steuerProzent: p.steuerProzent,
        hinweis: null,
      });
    } catch {
      aendere(z.nr, { hinweis: 'Preis nicht abrufbar — der Entwurf sucht ihn selbst.' });
    }
  }

  function zeileHinzu(art: 'artikel' | 'freitext'): void {
    const vorgabe = steuern.find((s) => s.schluessel === 'DE-19') ?? steuern[0];
    const neu: Zeile = {
      nr: naechste,
      art,
      artikel: undefined,
      bezeichnung: '',
      menge: '1',
      einzelpreis: '',
      vonHand: art === 'freitext',
      steuerschluessel: art === 'freitext' ? (vorgabe?.schluessel ?? '') : '',
      steuerProzent: art === 'freitext' ? (vorgabe?.prozent ?? '') : '',
      hinweis: null,
      nachlass: KEIN_NACHLASS,
      leistungVon: '',
      leistungBis: '',
    };
    setzeZeilen((alt) => [...alt, neu]);
    setzeNaechste((n) => n + 1);
    if (art === 'artikel') setzeWahlFuer(neu.nr);
  }

  function kundeWaehlen(k: Kundenzeile): void {
    setzeKunde(k);
    // Kundenpreise und Preisführung hängen am Kunden: Katalogpreise neu holen.
    for (const z of zeilen) {
      if (z.art === 'artikel' && z.artikel !== undefined && !z.vonHand) void katalogpreis(z, z.artikel, k);
    }
  }

  const summen = vorschausummen(
    zeilen.map((z) => ({ menge: Number(z.menge), einzelpreis: preisLesbar(z.einzelpreis), steuerProzent: z.steuerProzent })),
    fuehrung,
  );
  const bisStimmt = leistungBis === '' || leistungBis >= leistungVon;
  const gesamtrabatt = alsRabatt(gesamtNachlass);
  // Ein Gutschein, dessen Zeile entfernt wurde, hängt an nichts mehr.
  const gebundeneBg = bgGutscheine.filter((g) => zeilen.some((z) => z.nr === g.zeileNr));
  /** Nur zum Prüfen eines Codes: vorläufiges Brutto abzüglich der Kostenträger-Gutscheine. */
  const vorlaeufigOffen = restbetrag(summen.brutto, [
    ...gebundeneBg.map((g) => g.betrag),
    ...wertgutscheine.map((w) => w.anrechnung),
  ]);
  /** Spätester Leistungstag — gegen ihn prüft der Server die Gültigkeit (GUT-08). */
  const spaetesterTag = [leistungBis, leistungVon, ...zeilen.flatMap((z) => [z.leistungVon, z.leistungBis])]
    .filter((t) => t !== '')
    .sort()
    .at(-1);
  const bereit =
    kunde !== undefined &&
    betreff.trim() !== '' &&
    leistungVon !== '' &&
    bisStimmt &&
    gesamtrabatt !== null &&
    zeilen.length > 0 &&
    zeilen.every(zeileVollstaendig);

  async function absenden(): Promise<void> {
    if (!bereit || kunde === undefined) return;
    setzeLaeuft(true);
    setzeFehler(undefined);
    try {
      const neu = await ausNeuemAuftrag({
        kunde: kunde.id,
        betreff: betreff.trim(),
        belegdatum: heute(),
        bestellreferenz: referenz.trim() === '' ? null : referenz.trim(),
        leistungVon,
        leistungBis: leistungBis === '' ? null : leistungBis,
        positionen: zeilen.map(alsDirektzeile),
        ...(kopftext.trim() === '' ? {} : { kopftext: kopftext.trim() }),
        ...(fusstext.trim() === '' ? {} : { fusstext: fusstext.trim() }),
        ...(gesamtrabatt == null ? {} : { gesamtrabatt }),
        ...(empfaenger === '' ? {} : { empfaengerKontakt: empfaenger }),
        // PRD-38: die Zeile als Stelle in den Positionen — so bindet der Server an die Auftragsposition.
        bgGutscheine: gebundeneBg.map((g) => ({
          zeile: zeilen.findIndex((z) => z.nr === g.zeileNr),
          kostentraeger: g.kostentraeger.id,
          nummer: g.nummer,
          name: g.name,
          gutscheinwert: g.gutscheinwert?.id ?? null,
          betrag: g.betrag,
        })),
        // GUT-14: erst hier eingelöst, gebunden an den Beleg.
        wertgutscheine: wertgutscheine.map((w) => ({ code: w.code })),
      });
      beiAngelegt(neu.id);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    } finally {
      setzeLaeuft(false);
    }
  }

  const wahlZeile = zeilen.find((z) => z.nr === wahlFuer);

  return (
    <div className="maske maske--weit erfassung">
      {fehler !== undefined && <Meldung fehler={fehler} />}

      {waehlt && (
        <Kundensuchfenster
          beiWahl={kundeWaehlen}
          beiFehler={setzeFehler}
          beiSchliessen={() => setzeWaehlt(false)}
        />
      )}

      {/* ÄA-41, Seitentyp Erfassung. M-61: Erfassen, dann „Anlegen und abrechnen". Das Suchfenster steht außerhalb der Karte. */}
      <Erfassung
        beschriftung="Neue Rechnung"
        beiAbsenden={() => void absenden()}
        absenden={{
          text: laeuft ? 'Wird angelegt …' : 'Anlegen und abrechnen',
          gesperrt: !bereit || laeuft,
        }}
        abbrechen={{ text: 'Abbrechen', beiKlick: beiAbbruch }}
      >
        {/* BUC-75: zuerst der Auftrag */}
        <Abschnitt titel="Rechnung" zusatz="Auftrag und Entwurf entstehen zusammen">
          <Feldraster>
            <Kundenfeld
              kennung="rechnung-kunde"
              kunde={kunde}
              offen={waehlt}
              beiSuchen={() => setzeWaehlt(true)}
            >
              {/* AUF-02, AUF-21 */}
              <p className="feld__hilfe">
                Zahlungsziel und Skonto kommen aus der Kundenkondition. Es entsteht ein Auftrag
                (Herkunft Innendienst), der mit der Rechnung verbunden bleibt.
              </p>
            </Kundenfeld>

            <div className="feld">
              <label className="feld__beschriftung" htmlFor="rechnung-betreff">
                Betreff
              </label>
              <input
                id="rechnung-betreff"
                type="text"
                value={betreff}
                maxLength={200}
                onChange={(e) => setzeBetreff(e.target.value)}
                placeholder="Fahrsicherheitstraining vom 12. Oktober"
              />
            </div>

            <div className="feld">
              <label className="feld__beschriftung" htmlFor="rechnung-referenz">
                Bestellreferenz des Kunden
              </label>
              <input
                id="rechnung-referenz"
                type="text"
                value={referenz}
                maxLength={80}
                onChange={(e) => setzeReferenz(e.target.value)}
                placeholder="Bestellnummer, Kostenstelle"
              />
            </div>

            {/* BP-33: der Leistungszeitpunkt steht auf jeder Rechnung */}
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="rechnung-leistung-von">
                Leistungsdatum
              </label>
              <input
                id="rechnung-leistung-von"
                type="date"
                value={leistungVon}
                onChange={(e) => setzeLeistungVon(e.target.value)}
              />
            </div>

            <div className="feld">
              <label className="feld__beschriftung" htmlFor="rechnung-leistung-bis">
                Leistung bis (bei einem Zeitraum)
              </label>
              <input
                id="rechnung-leistung-bis"
                type="date"
                value={leistungBis}
                min={leistungVon}
                onChange={(e) => setzeLeistungBis(e.target.value)}
              />
              {!bisStimmt && <p className="feld__hilfe">Das Ende liegt vor dem Leistungsdatum.</p>}
            </div>

            {/* KUN-08, BUC-103: ein Ansprechpartner des Kunden; die Anschrift bleibt die Rechnungsanschrift. */}
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="rechnung-empfaenger">
                Zu Händen
              </label>
              <select
                id="rechnung-empfaenger"
                value={empfaenger}
                disabled={kontakte.length === 0}
                onChange={(e) => setzeEmpfaenger(e.target.value)}
              >
                <option value="">{kontakte.length === 0 ? 'Kein Ansprechpartner hinterlegt' : 'Ohne „z. Hd.“'}</option>
                {kontakte.map((k) => (
                  <option key={k.kontaktrolle} value={k.kontaktrolle}>
                    {k.name}
                    {k.rechnungsempfaenger ? ' (Rechnungsempfänger)' : ''}
                  </option>
                ))}
              </select>
            </div>
          </Feldraster>
          {/* DAT-12, AeA-64: nur ein Hinweis — verrechnet wird als eigener Vorgang mit Rückfrage. */}
          {guthaben !== undefined && (
            <p className="hinweiszeile" role="status">
              Der Kunde hat ein Guthaben von {alsEuro(guthaben)}. Verrechnet wird es nach dem Festschreiben in der
              Kundenakte oder am Gutschriftbeleg („Verrechnen“), mit dieser Rechnung als Ziel.
            </p>
          )}
        </Abschnitt>

        {/* RNG-01: Kopf- und Schlusstext stehen auf dem Beleg. */}
        <Abschnitt titel="Texte" zusatz="Stehen über und unter den Positionen" anfangsOffen={false}>
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="rechnung-kopftext">
              Kopftext
            </label>
            <textarea id="rechnung-kopftext" rows={3} maxLength={8000} value={kopftext} onChange={(e) => setzeKopftext(e.target.value)} />
          </div>
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="rechnung-fusstext">
              Schlusstext
            </label>
            <textarea id="rechnung-fusstext" rows={3} maxLength={8000} value={fusstext} onChange={(e) => setzeFusstext(e.target.value)} />
          </div>
        </Abschnitt>

        <Abschnitt titel="Positionen" zusatz={fuehrung === 'brutto' ? 'Preise brutto (Privatkunde)' : 'Preise netto'}>
          {zeilen.length > 0 && (
            <div className="tabellenhuelle tabellenhuelle--telefon">
              <table className="liste__tabelle">
                <thead>
                  <tr>
                    <th scope="col">Leistung</th>
                    <th scope="col" className="zahl">Menge</th>
                    <th scope="col" className="zahl">Einzelpreis</th>
                    <th scope="col">Steuer</th>
                    <th scope="col" className="zahl">Betrag</th>
                    <th scope="col">
                      <span className="nur-vorlesen">Entfernen</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {zeilen.map((z, i) => (
                    <Fragment key={z.nr}>
                    <tr>
                      <td>
                        {z.art === 'artikel' ? (
                          <>
                            {z.artikel === undefined ? '—' : `${z.artikel.artikelnummer} · ${z.artikel.bezeichnung}`}{' '}
                            <button type="button" className="knopf knopf--klein" onClick={() => setzeWahlFuer(z.nr)}>
                              {z.artikel === undefined ? 'Artikel wählen' : 'Ändern'}
                            </button>
                            {z.hinweis !== null && <p className="feld__hilfe">{z.hinweis}</p>}
                          </>
                        ) : (
                          <input
                            type="text"
                            aria-label={`Bezeichnung Position ${String(i + 1)}`}
                            value={z.bezeichnung}
                            maxLength={400}
                            onChange={(e) => aendere(z.nr, { bezeichnung: e.target.value })}
                            placeholder="Was berechnet wird"
                          />
                        )}
                      </td>
                      <td className="zahl">
                        <input
                          type="number"
                          min={1}
                          step={1}
                          aria-label={`Menge Position ${String(i + 1)}`}
                          value={z.menge}
                          onChange={(e) => aendere(z.nr, { menge: e.target.value })}
                        />
                      </td>
                      <td className="zahl">
                        <input
                          type="text"
                          inputMode="decimal"
                          aria-label={`Einzelpreis Position ${String(i + 1)}`}
                          value={z.einzelpreis}
                          onChange={(e) => aendere(z.nr, { einzelpreis: e.target.value, vonHand: true })}
                        />
                      </td>
                      <td>
                        {z.art === 'freitext' ? (
                          <select
                            aria-label={`Steuer Position ${String(i + 1)}`}
                            value={z.steuerschluessel}
                            onChange={(e) => {
                              const s = steuern.find((x) => x.schluessel === e.target.value);
                              aendere(z.nr, { steuerschluessel: e.target.value, steuerProzent: s?.prozent ?? '' });
                            }}
                          >
                            {z.steuerschluessel === '' && <option value="">Bitte wählen</option>}
                            {steuern.map((s) => (
                              <option key={s.schluessel} value={s.schluessel}>
                                {s.bezeichnung} ({alsProzent(s.prozent)})
                              </option>
                            ))}
                          </select>
                        ) : z.steuerProzent === '' ? (
                          '—'
                        ) : (
                          `${z.steuerschluessel} · ${alsProzent(z.steuerProzent)}`
                        )}
                      </td>
                      <td className="zahl">
                        {alsEuro(zeilenbetrag({ menge: Number(z.menge), einzelpreis: preisLesbar(z.einzelpreis), steuerProzent: z.steuerProzent }))}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="knopf knopf--klein knopf--eingriff"
                          onClick={() => setzeZeilen((alt) => alt.filter((x) => x.nr !== z.nr))}
                        >
                          Entfernen
                        </button>
                      </td>
                    </tr>
                    {/* CRM-174: Nachlass (Stufe 6 oder eigene Nachlasszeile) und Leistungsdatum der Zeile */}
                    <tr>
                      <td colSpan={6}>
                        <span className="knopfreihe">
                          <select
                            aria-label={`Nachlass Position ${String(i + 1)}`}
                            value={z.nachlass.art}
                            onChange={(e) =>
                              aendere(z.nr, { nachlass: { art: e.target.value as Nachlass['art'], wert: z.nachlass.wert } })
                            }
                          >
                            <option value="kein">Kein Nachlass</option>
                            <option value="prozent">Nachlass in %</option>
                            <option value="betrag">Nachlass in €</option>
                          </select>
                          {z.nachlass.art !== 'kein' && (
                            <input
                              type="text"
                              inputMode="decimal"
                              aria-label={`Nachlasswert Position ${String(i + 1)}`}
                              value={z.nachlass.wert}
                              onChange={(e) => aendere(z.nr, { nachlass: { art: z.nachlass.art, wert: e.target.value } })}
                            />
                          )}
                          <input
                            type="date"
                            aria-label={`Leistungsdatum Position ${String(i + 1)}`}
                            value={z.leistungVon}
                            onChange={(e) => aendere(z.nr, { leistungVon: e.target.value })}
                          />
                          {z.leistungVon !== '' && (
                            <input
                              type="date"
                              aria-label={`Leistung bis Position ${String(i + 1)}`}
                              value={z.leistungBis}
                              min={z.leistungVon}
                              onChange={(e) => aendere(z.nr, { leistungBis: e.target.value })}
                            />
                          )}
                        </span>
                        {alsRabatt(z.nachlass) === null && <p className="feld__hilfe">Der Nachlass ist nicht lesbar.</p>}
                        {z.leistungVon === '' && <p className="feld__hilfe">Ohne Datum gilt der Leistungszeitraum der Rechnung.</p>}
                      </td>
                    </tr>
                    </Fragment>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th scope="row" colSpan={4}>Netto</th>
                    <td className="zahl">{alsEuro(summen.netto)}</td>
                    <td />
                  </tr>
                  <tr>
                    <th scope="row" colSpan={4}>Steuer</th>
                    <td className="zahl">{alsEuro(summen.steuer)}</td>
                    <td />
                  </tr>
                  <tr>
                    <th scope="row" colSpan={4}>Brutto</th>
                    <td className="zahl">
                      <strong>{alsEuro(summen.brutto)}</strong>
                    </td>
                    <td />
                  </tr>
                  {/* Erfasst, nicht gerechnet: Nachlass und Anrechnung rechnet der Entwurf. */}
                  {gesamtrabatt != null && (
                    <tr>
                      <th scope="row" colSpan={4}>Nachlass auf die Rechnung</th>
                      <td className="zahl">{nachlassLesbar(gesamtNachlass)}</td>
                      <td />
                    </tr>
                  )}
                  {gebundeneBg.map((g) => (
                    <tr key={`bg-${g.kostentraeger.id}-${g.nummer}`}>
                      <th scope="row" colSpan={4}>
                        abzüglich Gutschein {g.kostentraeger.name} Nr. {g.nummer} ({g.name})
                      </th>
                      <td className="zahl">−{alsEuro(g.betrag)}</td>
                      <td>
                        <button
                          type="button"
                          className="knopf knopf--klein knopf--eingriff"
                          onClick={() => setzeBgGutscheine((alt) => alt.filter((x) => x !== g))}
                        >
                          Entfernen
                        </button>
                      </td>
                    </tr>
                  ))}
                  {wertgutscheine.map((w) => (
                    <tr key={`wert-${w.gutschein}`}>
                      <th scope="row" colSpan={4}>
                        abzüglich Wertgutschein {w.nummer} (Restwert {alsEuro(w.restwert)})
                      </th>
                      <td className="zahl">−{alsEuro(w.anrechnung)}</td>
                      <td>
                        <button
                          type="button"
                          className="knopf knopf--klein knopf--eingriff"
                          onClick={() => setzeWertgutscheine((alt) => alt.filter((x) => x !== w))}
                        >
                          Entfernen
                        </button>
                      </td>
                    </tr>
                  ))}
                </tfoot>
              </table>
            </div>
          )}
          <p className="feld__hilfe">
            {zeilen.length === 0
              ? 'Noch keine Position. Eine Rechnung braucht mindestens eine.'
              : summen.vollstaendig
                ? 'Vorläufige Summen — verbindlich rechnet der Entwurf.'
                : 'Vorläufige Summen ohne die Zeilen, denen Preis oder Steuer fehlt.'}
            {(gesamtrabatt != null || zeilen.some((z) => alsRabatt(z.nachlass) != null) || gebundeneBg.length > 0 || wertgutscheine.length > 0) &&
              ' Nachlässe und Gutscheine sind erfasst, nicht eingerechnet — Rechnungs- und Zahlbetrag zeigt der Entwurf.'}
          </p>
          {/* ANG-02, RND-09: der Nachlass auf alles, je Steuersatz verteilt; die Kundenkondition gilt vorab. */}
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="rechnung-gesamtnachlass">
              Nachlass auf die Rechnung
            </label>
            <span className="knopfreihe">
              <select
                id="rechnung-gesamtnachlass"
                value={gesamtNachlass.art}
                onChange={(e) => setzeGesamtNachlass({ art: e.target.value as Nachlass['art'], wert: gesamtNachlass.wert })}
              >
                <option value="kein">Kein Nachlass</option>
                <option value="prozent">in %</option>
                <option value="betrag">in €</option>
              </select>
              {gesamtNachlass.art !== 'kein' && (
                <input
                  type="text"
                  inputMode="decimal"
                  aria-label="Nachlasswert auf die Rechnung"
                  value={gesamtNachlass.wert}
                  onChange={(e) => setzeGesamtNachlass({ art: gesamtNachlass.art, wert: e.target.value })}
                />
              )}
            </span>
            <p className="feld__hilfe">
              {gesamtrabatt === null
                ? 'Der Nachlass ist nicht lesbar.'
                : 'Kundenpreise und Rabattgruppe aus der Kundenkondition gelten schon; der Nachlass kommt hinzu.'}
            </p>
          </div>
          <div className="knopfreihe">
            <button type="button" className="knopf" onClick={() => zeileHinzu('artikel')}>
              Artikel hinzufügen
            </button>
            <button type="button" className="knopf" onClick={() => zeileHinzu('freitext')}>
              Freitext hinzufügen
            </button>
            <button type="button" className="knopf" disabled={zeilen.length === 0} onClick={() => setzeGutscheinOffen(true)}>
              Gutschein anrechnen
            </button>
          </div>
        </Abschnitt>
      </Erfassung>

      {gutscheinOffen && (
        <GutscheinAnrechnen
          zeilen={zeilen.map((z, i) => ({
            nr: z.nr,
            text: `${String(i + 1)}: ${z.art === 'artikel' ? (z.artikel?.bezeichnung ?? '—') : z.bezeichnung || 'Freitext'}`,
          }))}
          offenerBetrag={vorlaeufigOffen}
          leistungstag={spaetesterTag ?? null}
          schonGeprueft={wertgutscheine.map((w) => w.gutschein)}
          beiAnrechnen={(a) => {
            if (a.art === 'bg') setzeBgGutscheine((alt) => [...alt, a]);
            else setzeWertgutscheine((alt) => [...alt, a]);
            setzeGutscheinOffen(false);
          }}
          beiSchliessen={() => setzeGutscheinOffen(false)}
        />
      )}

      {wahlZeile !== undefined && (
        <Artikelwahl
          beiSchliessen={() => {
            // Eine neue Zeile ohne Artikel ist nichts — sie geht mit der Wahl.
            if (wahlZeile.artikel === undefined) setzeZeilen((alt) => alt.filter((x) => x.nr !== wahlZeile.nr));
            setzeWahlFuer(undefined);
          }}
          beiWahl={(gewaehlt) => {
            const z = { ...wahlZeile, artikel: gewaehlt, vonHand: false };
            aendere(wahlZeile.nr, { artikel: gewaehlt, vonHand: false, hinweis: null });
            setzeWahlFuer(undefined);
            void katalogpreis(z, gewaehlt, kunde);
          }}
        />
      )}
    </div>
  );
}
